import { defineStore } from "pinia";
import type {
  MenuItem,
  PermissionAction,
  PermissionResponse,
} from "../types/permission";

export const usePermissionStore = defineStore("permission", {
  state: () => ({
    menus: [] as MenuItem[],
    clientMenus: [] as MenuItem[],
    ibMenus: [] as MenuItem[],
    actions: {} as { [key: string]: PermissionAction },
    clientActions: {} as { [key: string]: PermissionAction },
    ibActions: {} as { [key: string]: PermissionAction },
    permissions: [] as string[],
    clientPermissions: [] as string[],
    ibPermissions: [] as string[],
  }),

  actions: {
    // 初始化权限数据
    async initPermissions() {
      try {
        // @ts-ignore
        const response = await window.request("AuthGetUserAction");
        const data: PermissionResponse = response.data;
        this.menus = this.filterMenus(data.menus);
        this.actions = data.actions;
        // 从actions中提取按钮权限列表
        this.permissions = Object.keys(data.actions || {});
      } catch (error) {
        this.menus = [];
        this.actions = {};
        this.permissions = [];
      }
    },

    // 初始化Client权限数据
    async initClientPermissions() {
      try {
        // @ts-ignore
        const response = await window.request("GetUserMenuPermissions");
        const data: PermissionResponse = response.data;
        this.clientMenus = this.filterMenus(data.menus);
        this.clientActions = data.actions;
        this.clientPermissions = Object.keys(data.actions || {});
      } catch (error) {
        this.clientMenus = [];
        this.clientActions = {};
        this.clientPermissions = [];
      }
    },

    // 初始化IB权限数据
    async initIbPermissions() {
      try {
        // @ts-ignore
        const response = await window.request("GetUserMenuPermissions");
        const data: PermissionResponse = response.data;
        // 过滤菜单数据，移除 status 为 0 的项
        this.ibMenus = this.filterMenus(data.menus);
        this.ibActions = data.actions;

        // 从actions中提取按钮权限列表
        this.ibPermissions = Object.keys(data.actions || {});
      } catch (error) {
        this.ibMenus = [];
        this.ibActions = {};
        this.ibPermissions = [];
      }
    },

    // 检查是否有某个权限
    hasPermission(permission: string): boolean {
      return this.permissions.includes(permission);
    },

    // 检查Client是否有某个权限
    hasClientPermission(permission: string): boolean {
      return this.clientPermissions.includes(permission);
    },

    // 检查IB是否有某个权限
    hasIbPermission(permission: string): boolean {
      return this.ibPermissions.includes(permission);
    },

    // 检查是否有多个权限中的任意一个
    hasAnyPermission(permissions: string[]): boolean {
      return permissions.some((permission) => this.hasPermission(permission));
    },

    // 检查Client是否有多个权限中的任意一个
    hasAnyClientPermission(permissions: string[]): boolean {
      return permissions.some((permission) =>
        this.hasClientPermission(permission)
      );
    },

    // 检查IB是否有多个权限中的任意一个
    hasAnyIbPermission(permissions: string[]): boolean {
      return permissions.some((permission) => this.hasIbPermission(permission));
    },

    // 检查是否同时拥有多个权限
    hasAllPermissions(permissions: string[]): boolean {
      return permissions.every((permission) => this.hasPermission(permission));
    },

    // 检查Client是否同时拥有多个权限
    hasAllClientPermissions(permissions: string[]): boolean {
      return permissions.every((permission) =>
        this.hasClientPermission(permission)
      );
    },

    // 检查IB是否同时拥有多个权限
    hasAllIbPermissions(permissions: string[]): boolean {
      return permissions.every((permission) =>
        this.hasIbPermission(permission)
      );
    },

    // 获取指定权限的详细信息
    getPermissionDetail(permission: string): PermissionAction | undefined {
      return this.actions[permission];
    },

    // 获取Client指定权限的详细信息
    getClientPermissionDetail(
      permission: string
    ): PermissionAction | undefined {
      return this.clientActions[permission];
    },

    // 获取IB指定权限的详细信息
    getIbPermissionDetail(permission: string): PermissionAction | undefined {
      return this.ibActions[permission];
    },

    // 获取菜单树
    getMenus(): MenuItem[] {
      return this.menus;
    },

    // 获取Client菜单树
    getClientMenus(): MenuItem[] {
      return this.clientMenus;
    },

    // 获取IB菜单树
    getIbMenus(): MenuItem[] {
      return this.ibMenus;
    },

    // 过滤菜单，移除 status 为 0 的项
    filterMenus(menus: MenuItem[]): MenuItem[] {
      if (!menus) return [];

      return menus.filter((menu) => {
        // 如果 status 为 0，则过滤掉该项
        if (menu.status === 0) {
          return false;
        }

        // 如果有子菜单，递归过滤
        if (menu.children && menu.children.length > 0) {
          menu.children = this.filterMenus(menu.children);
          // 如果过滤后没有可访问的子菜单，则不显示父菜单
          return menu.children.length > 0;
        }

        return true;
      });
    },
  },
});
